import type { ProjectDocumentDto } from '@/lib/api/v2/projects'

/**
 * SOLY-DOC-002 — pure helpers behind the chantier documents card: display order, what the viewer
 * can render, the print order of a selection and the dossier's file name.
 */

export type DocumentSort = 'newest' | 'oldest' | 'name'

export const DOCUMENT_SORT_LABELS: Record<DocumentSort, string> = {
  newest: 'Plus récent en premier',
  oldest: 'Plus ancien en premier',
  name: 'Nom (A → Z)',
}

export function sortDocuments(documents: readonly ProjectDocumentDto[], sort: DocumentSort): ProjectDocumentDto[] {
  const byDate = (a: ProjectDocumentDto, b: ProjectDocumentDto) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  const copy = [...documents]
  switch (sort) {
    case 'oldest':
      return copy.sort((a, b) => byDate(a, b) || a.displayName.localeCompare(b.displayName, 'fr'))
    case 'name':
      return copy.sort(
        (a, b) => a.displayName.localeCompare(b.displayName, 'fr', { numeric: true, sensitivity: 'base' }) || byDate(a, b)
      )
    case 'newest':
    default:
      return copy.sort((a, b) => byDate(b, a) || a.displayName.localeCompare(b.displayName, 'fr'))
  }
}

export type PreviewKind = 'pdf' | 'image' | 'unsupported'

/** Raster/vector formats every desktop browser renders in an <img>. HEIC/TIFF are not among them. */
const BROWSER_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/avif'])

export function previewKind(contentType: string): PreviewKind {
  const type = contentType.trim().toLowerCase()
  if (type === 'application/pdf') return 'pdf'
  if (BROWSER_IMAGE_TYPES.has(type)) return 'image'
  return 'unsupported'
}

/** The selected ids, in the order the table shows them — what you see is what prints. */
export function selectionInDisplayOrder(displayed: readonly ProjectDocumentDto[], selected: ReadonlySet<string>): string[] {
  return displayed.filter((d) => selected.has(d.id)).map((d) => d.id)
}

/** `Dossier chantier - Cuisine Dupont - 2026-11-03.pdf`, without characters Windows refuses. */
export function bundleFileName(projectName: string | null | undefined, date: Date = new Date()): string {
  return chantierFileName('Dossier chantier', projectName, date, 'pdf')
}

/** `Documents chantier - Cuisine Dupont - 2026-11-04.zip`: the ZIP of the original files. */
export function archiveFileName(projectName: string | null | undefined, date: Date = new Date()): string {
  return chantierFileName('Documents chantier', projectName, date, 'zip')
}

function chantierFileName(prefix: string, projectName: string | null | undefined, date: Date, extension: string): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const name = (projectName ?? '')
    .replace(/[<>:"/\\|?*]/g, ' ')
    .split('')
    .filter((c) => c.charCodeAt(0) >= 32)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
  return name ? `${prefix} - ${name} - ${day}.${extension}` : `${prefix} - ${day}.${extension}`
}

/** The name a saved copy gets: the display name, keeping the original extension after a rename. */
export function documentDownloadName(doc: Pick<ProjectDocumentDto, 'displayName' | 'fileName'>): string {
  const dot = doc.fileName.lastIndexOf('.')
  const extension = dot > 0 ? doc.fileName.slice(dot) : ''
  return extension && !doc.displayName.toLowerCase().endsWith(extension.toLowerCase())
    ? `${doc.displayName}${extension}`
    : doc.displayName
}
